<template>
    <v-row no-gutters class="d-block">
        <v-col cols="12">
            <!-- Credenciales -->
            <v-card>
                <v-card-title class="text-subtitle-1 font-weight-bold pb-0">
                    <v-icon left color="primary">vpn_key</v-icon>
                    {{ tieneUsuario ? 'Credenciales' : 'Crear credenciales' }}
                </v-card-title>
                <v-card-text>
                    <v-alert dense text type="info" class="mb-4" v-if="!resumen.es_cuenta_propia">
                        La contraseña que asigne aquí es <b>temporal</b>: vence en 72 horas y la persona deberá
                        cambiarla por una propia en su primer ingreso.
                    </v-alert>

                    <v-form ref="form" v-model="valid">
                        <v-row>
                            <v-col cols="12" sm="6">
                                <v-text-field
                                    v-model="usuarioModel.usuario"
                                    :label="usuarioForm.usuario.label"
                                    :hint="tieneUsuario ? usuarioForm.usuario.hint : ''"
                                    persistent-hint
                                    :placeholder="usuarioForm.usuario.placeholder"
                                    required
                                    prepend-icon="account_circle"
                                    :rules="usuarioForm.usuario.rules"
                                />
                            </v-col>

                            <v-col cols="12" sm="6">
                                <v-text-field
                                    :type="hidePassword ? 'password' : 'text'"
                                    v-model="usuarioModel.password"
                                    :label="resumen.es_cuenta_propia ? 'Contraseña nueva' : 'Contraseña temporal'"
                                    :append-icon="hidePassword ? 'visibility_off' : 'visibility'"
                                    prepend-icon="vpn_key"
                                    required
                                    :rules="reglasPassword"
                                    @click:append="hidePassword = !hidePassword"/>

                                <v-text-field
                                    :type="hidePassword ? 'password' : 'text'"
                                    v-model="usuarioModel.password_confirmar"
                                    label="Confirmar la contraseña"
                                    prepend-icon="vpn_key"
                                    required
                                    :rules="[(v) => v === usuarioModel.password || 'Las contraseñas deben coincidir.']"/>

                                <v-btn small text color="primary" class="ml-6" @click="generarTemporal" v-if="!resumen.es_cuenta_propia">
                                    <v-icon small left>casino</v-icon> Generar contraseña temporal
                                </v-btn>
                            </v-col>
                        </v-row>
                    </v-form>
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions class="justify-center py-3">
                    <v-btn outlined @click="regresar" small>
                        <v-icon>arrow_back</v-icon>
                        Regresar
                    </v-btn>

                    <v-btn outlined color="warning" @click="reset" small>
                        <v-icon>restart_alt</v-icon>
                        Limpiar
                    </v-btn>

                    <v-btn color="success" :loading="loading" @click="saveUsuario" small>
                        <v-icon>save</v-icon>
                        Guardar
                    </v-btn>
                </v-card-actions>
            </v-card>

            <!-- Seguridad de la cuenta -->
            <v-card class="mt-4" v-if="tieneUsuario">
                <v-card-title class="text-subtitle-1 font-weight-bold pb-0">
                    <v-icon left color="primary">security</v-icon>
                    Seguridad de la cuenta
                </v-card-title>
                <v-card-text>
                    <v-row>
                        <v-col cols="12" md="6">
                            <div class="dato">
                                <span class="dato__titulo">Último acceso</span>
                                <span v-if="resumen.ultimo_acceso">{{ resumen.ultimo_acceso | fechaESPHora }} <span class="grey--text" v-if="resumen.ultimo_acceso_ip">· IP {{ resumen.ultimo_acceso_ip }}</span></span>
                                <span v-else class="grey--text">Nunca ha ingresado</span>
                            </div>
                            <div class="dato">
                                <span class="dato__titulo">Contraseña</span>
                                <v-chip x-small label :color="estadoPassword.color" text-color="white">{{ estadoPassword.texto }}</v-chip>
                                <span class="grey--text ml-1" v-if="resumen.debe_cambiar_password && resumen.password_temporal_expira">
                                    vence {{ resumen.password_temporal_expira | fechaESPHora }}
                                </span>
                            </div>
                            <div class="dato">
                                <span class="dato__titulo">Inactividad</span>
                                <span>Se desactiva sola tras {{ resumen.dias_inactividad }} días sin ingresar. Solo un administrador puede reactivarla.</span>
                            </div>

                            <div class="mt-4" v-if="!resumen.es_cuenta_propia">
                                <v-btn small outlined color="primary" :loading="restableciendo" @click="confirmarRestablecer = true">
                                    <v-icon small left>lock_reset</v-icon> Restablecer contraseña
                                </v-btn>
                                <div class="text-caption grey--text text--darken-1 mt-1">
                                    Genera una contraseña temporal nueva y cierra las sesiones abiertas de la persona.
                                </div>
                            </div>
                        </v-col>

                        <v-col cols="12" md="6">
                            <v-select
                                v-model="metodo"
                                :items="metodos"
                                item-text="texto"
                                item-value="valor"
                                label="Método de autenticación"
                                prepend-icon="verified_user"
                                outlined dense
                                :hint="(metodos.find(m => m.valor === metodo) || {}).ayuda"
                                persistent-hint
                            >
                                <template v-slot:item="{ item }">
                                    <v-list-item-icon class="mr-3"><v-icon>{{ item.icono }}</v-icon></v-list-item-icon>
                                    <v-list-item-content>
                                        <v-list-item-title>{{ item.texto }}</v-list-item-title>
                                        <v-list-item-subtitle>{{ item.ayuda }}</v-list-item-subtitle>
                                    </v-list-item-content>
                                </template>
                            </v-select>

                            <v-alert dense text type="warning" class="mt-3 mb-0" v-if="usaCorreo && !resumen.correo_disponible">
                                El envío de correo todavía no está configurado en el servidor, así que por ahora
                                solo se puede usar la aplicación autenticadora.
                            </v-alert>
                            <v-alert dense text type="warning" class="mt-3 mb-0" v-else-if="usaCorreo && !resumen.correo">
                                Esta persona no tiene correo en sus datos personales.
                            </v-alert>

                            <div class="d-flex flex-wrap align-center mt-3">
                                <v-btn small color="success" :loading="guardandoMetodo"
                                       :disabled="metodo === resumen.metodo_autenticacion" @click="guardarMetodo">
                                    <v-icon small left>save</v-icon> Guardar método
                                </v-btn>

                                <template v-if="usaApp(resumen.metodo_autenticacion)">
                                    <v-chip small outlined class="ml-3" :color="resumen.app_configurada ? 'success' : 'grey'">
                                        <v-icon small left>{{ resumen.app_configurada ? 'phonelink_lock' : 'phonelink_erase' }}</v-icon>
                                        {{ resumen.app_configurada ? 'Aplicación enlazada' : 'Aplicación sin enlazar' }}
                                    </v-chip>
                                    <v-btn small text color="error" class="ml-1" v-if="resumen.app_configurada"
                                           :loading="restableciendoApp" @click="restablecerApp"
                                           title="Úselo si la persona perdió o cambió su teléfono">
                                        Desvincular
                                    </v-btn>
                                </template>
                            </div>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>
        </v-col>

        <!-- Confirmar restablecimiento -->
        <v-dialog v-model="confirmarRestablecer" max-width="440">
            <v-card>
                <v-card-title class="text-subtitle-1 font-weight-bold">¿Restablecer la contraseña de {{ usuarioModel.usuario }}?</v-card-title>
                <v-card-text>
                    La contraseña actual dejará de servir, se cerrarán sus sesiones abiertas y se generará una
                    contraseña temporal que deberá entregarle.
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text small @click="confirmarRestablecer = false">Cancelar</v-btn>
                    <v-btn color="primary" small :loading="restableciendo" @click="restablecerPassword">Restablecer</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Contraseña temporal generada: se muestra una sola vez -->
        <v-dialog v-model="mostrarTemporal" max-width="460" persistent>
            <v-card>
                <v-card-title class="text-subtitle-1 font-weight-bold">
                    <v-icon left color="success">check_circle</v-icon> Contraseña temporal generada
                </v-card-title>
                <v-card-text>
                    <p class="mb-2">Entréguela a <b>{{ usuarioModel.usuario }}</b> por un medio seguro. No se volverá a mostrar.</p>
                    <div class="temporal">
                        <code>{{ temporalGenerada }}</code>
                        <v-btn icon small @click="copiar(temporalGenerada)" title="Copiar"><v-icon small>content_copy</v-icon></v-btn>
                    </div>
                    <p class="text-caption grey--text text--darken-1 mt-3 mb-0">
                        Vence {{ temporalExpira | fechaESPHora }}. Al ingresar, el sistema le pedirá cambiarla.
                    </p>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" small @click="cerrarTemporal">Ya la anoté</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-row>
</template>

<script>
import {reglasPassword, generarPasswordTemporal} from "@/services/password";

export default {
    name: "ParticipantesAdministradoresAccesos",
    props: {
        getParams: {
            type: Object,
            required: true,
        }
    },
    data() {
        return {
            valid: true,
            loading: false,
            hidePassword: true,
            reglasPassword,
            resumen: {},
            metodo: "password",
            guardandoMetodo: false,
            restableciendo: false,
            restableciendoApp: false,
            confirmarRestablecer: false,
            mostrarTemporal: false,
            temporalGenerada: "",
            temporalExpira: null,
            metodos: [
                {valor: "password", texto: "Solo contraseña", icono: "password", ayuda: "Sin segundo paso de verificación."},
                {valor: "app", texto: "Autenticación de dos factores (aplicación)", icono: "phonelink_lock", ayuda: "Código de Google o Microsoft Authenticator."},
                {valor: "correo", texto: "OTP por correo", icono: "mail", ayuda: "Código de un solo uso enviado a su correo."},
                {valor: "mixto", texto: "Mixto (aplicación o correo)", icono: "sync_lock", ayuda: "La persona elige con cuál verificar en cada ingreso."},
            ],
            usuarioModel: {
                persona_id: this.getParams.persona_id,
                usuario_id: null,
                usuario: null,
                password: null,
                password_confirmar: null,
            },
            minCarateres: 6,
            usuarioForm: {
                usuario: {
                    label: 'Nick de Usuario',
                    placeholder: 'Nick de Usuario',
                    hint: "Recomendamos no cambiar el Nick",
                    rules: [
                        (v) => !!v || 'Campo requerido',
                        (v) => !!v && v.length >= this.minCarateres || `El Nick debe tener más de ${this.minCarateres} caracteres.`
                    ],
                },
            },
        }
    },
    computed: {
        tieneUsuario() {
            return !!Number(this.usuarioModel.usuario_id);
        },
        usaCorreo() {
            return this.metodo === "correo" || this.metodo === "mixto";
        },
        estadoPassword() {
            if (this.resumen.password_temporal_vencida) return {texto: "Temporal vencida", color: "error"};
            if (this.resumen.debe_cambiar_password) return {texto: "Temporal, pendiente de cambio", color: "warning"};
            return {texto: "Definida por la persona", color: "success"};
        },
    },
    methods: {
        regresar() {
            this.$router.go(-1)
        },
        usaApp(metodo) {
            return metodo === "app" || metodo === "mixto";
        },
        reset() {
            this.$refs.form.reset();
            this.getUsuarioCreate();
        },
        generarTemporal() {
            const temporal = generarPasswordTemporal();
            this.usuarioModel.password = temporal;
            this.usuarioModel.password_confirmar = temporal;
            this.hidePassword = false;
        },
        getUsuarioCreate() {
            this.loading = true;

            let params = {persona_id: this.getParams.persona_id}

            window.axios.get('/cuentas/resumen', {params}).then(response => {
                if (response.data.success) {
                    this.resumen = response.data.data;
                    this.metodo = this.resumen.metodo_autenticacion;
                    this.usuarioModel.usuario = this.resumen.usuario;
                    this.usuarioModel.usuario_id = this.resumen.usuario_id;
                } else if (response.data.success === false) {
                    this.$toast.error(response.data.message);
                }
            })
                .catch(error => {
                    console.log(error)
                    this.$toast.error("No se pudo obtener los datos de la cuenta.");
                })
                .finally(() => {
                    this.loading = false;
                })
        },
        saveUsuario() {
            if (!this.$refs.form.validate()) {
                this.$toast.warning("Complete toda la información requerida.");
                return;
            }

            this.loading = true;
            let url = this.tieneUsuario ? '/usuarios/update' : '/usuarios/store';

            window.axios.post(url, this.usuarioModel).then(response => {
                if (response.data.success) {
                    this.$toast.success(response.data.message);
                    this.hidePassword = true;
                    this.reset();
                } else if (response.data.success === false) {
                    this.$toast.error(response.data.message);
                }
            })
                .catch(error => this.mostrarError(error, "No se pudo guardar"))
                .finally(() => {
                    this.loading = false;
                })
        },
        restablecerPassword() {
            this.restableciendo = true;

            window.axios.post('/cuentas/restablecer_password', {usuario_id: this.usuarioModel.usuario_id}).then(response => {
                if (response.data.success) {
                    this.confirmarRestablecer = false;
                    this.temporalGenerada = response.data.data.password_temporal;
                    this.temporalExpira = response.data.data.expira;
                    this.mostrarTemporal = true;
                } else {
                    this.$toast.error(response.data.message);
                }
            })
                .catch(error => this.mostrarError(error, "No se pudo restablecer la contraseña"))
                .finally(() => {
                    this.restableciendo = false;
                });
        },
        cerrarTemporal() {
            this.mostrarTemporal = false;
            this.temporalGenerada = "";
            this.getUsuarioCreate();
        },
        guardarMetodo() {
            this.guardandoMetodo = true;
            const params = {usuario_id: this.usuarioModel.usuario_id, metodo_autenticacion: this.metodo};

            window.axios.post('/cuentas/metodo', params).then(response => {
                if (response.data.success) {
                    this.$toast.success(response.data.message);
                    this.getUsuarioCreate();
                } else {
                    this.$toast.error(response.data.message);
                }
            })
                .catch(error => this.mostrarError(error, "No se pudo guardar el método"))
                .finally(() => {
                    this.guardandoMetodo = false;
                });
        },
        restablecerApp() {
            this.restableciendoApp = true;

            window.axios.post('/cuentas/restablecer_app', {usuario_id: this.usuarioModel.usuario_id}).then(response => {
                if (response.data.success) {
                    this.$toast.success(response.data.message);
                    this.getUsuarioCreate();
                } else {
                    this.$toast.error(response.data.message);
                }
            })
                .catch(error => this.mostrarError(error, "No se pudo desvincular la aplicación"))
                .finally(() => {
                    this.restableciendoApp = false;
                });
        },
        copiar(texto) {
            if (navigator.clipboard) {
                navigator.clipboard.writeText(texto).then(() => this.$toast.success("Copiada al portapapeles."));
            }
        },
        mostrarError(error, mensaje) {
            console.log(error);
            const cuerpo = error.response && error.response.data;
            if (cuerpo && cuerpo.data && typeof cuerpo.data === "object") {
                mensaje = Object.values(cuerpo.data).flat().join(" ") || mensaje;
            } else if (cuerpo && cuerpo.message) {
                mensaje = cuerpo.message;
            }
            this.$toast.error(mensaje);
        },
    },
    created() {
        this.getUsuarioCreate();
    }
}
</script>

<style scoped>
.dato {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px 8px;
    padding: 6px 0;
    border-bottom: 1px solid #eef0f6;
    font-size: 0.875rem;
}

.dato__titulo {
    min-width: 110px;
    font-weight: 600;
    color: #1b2340;
}

.temporal {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #f2f4fa;
    border: 1px dashed #b9c1da;
    border-radius: 8px;
    padding: 10px 14px;
}

.temporal code {
    font-size: 1.25rem;
    letter-spacing: 0.08em;
    background: transparent;
    color: #1b2340;
    box-shadow: none;
}
</style>
